import { createI18n } from 'vue-i18n';
import { getCookie } from '@/utils/cookie';
import { LOCAL_STORAGE_KEY } from '@/constants';
import store from '@/store';
import { inVite, isOverseasEnv } from '@/utils/api';

const LANGUAGE_KEY = LOCAL_STORAGE_KEY?.LANGUAGE || 'lang';

/** 动态加载失败时的回退语言，需与 createI18n 的 fallbackLocale 一致 */
const FALLBACK_LOCALE = 'zh-CN';

// 登录页必需模块（首屏最小集）
const LOGIN_MODULES = ['common', 'component', 'components', 'constants', 'login', 'routers', 'version', 'transfer'];

// 主布局必需模块（登录后、进入 /res 认证区域时加载）
const LAYOUT_MODULES = [
  'profile',
  'globalSearch',
  'meeting',
  'meetingRecording',
  'recorder',
  'selfSelected',
  'translate',
  'member',
  'aiNotes',
  'aiTools',
  'customService'
];

// 兼容旧引用
const CORE_MODULES = [...LOGIN_MODULES, ...LAYOUT_MODULES];

// 语言标准化函数
const normalizeLanguage = (lang) => {
  if (!lang) return 'zh-CN';

  const normalized = lang.toLowerCase();
  if (normalized === 'zh-cn' || normalized === 'zhcn' || normalized === 'zh_cn') {
    return 'zh-CN';
  } else if (normalized === 'zh-hant' || normalized === 'zhtc' || normalized === 'zh_tc') {
    return 'zh-TW';
  } else if (normalized === 'en' || normalized === 'en-us' || normalized === 'english') {
    return 'en-US';
  }
  return 'zh-CN'; // 默认返回简体中文
};

// 获取初始语言
const getInitialLocale = () => {
  // 优先从 cookie 获取
  const cookieValue = getCookie(LANGUAGE_KEY);
  if (cookieValue) {
    return normalizeLanguage(cookieValue);
  }

  // 其次从 localStorage 获取
  const localValue = localStorage.getItem('lang');
  if (localValue) {
    return normalizeLanguage(localValue);
  }

  // 如果是海外环境，则返回英文
  if (isOverseasEnv()) {
    return 'en-US';
  }

  // 最后从 navigator.language 获取
  const navigatorValue = navigator.language;
  if (navigatorValue) {
    return normalizeLanguage(navigatorValue);
  }

  // 默认返回中文
  return 'zh-CN';
};

const initialLocale = getInitialLocale();
store.commit('setLang', initialLocale);

// dev 环境下用于识别"路由漏声明 meta.localeModules"的运行时兜底
const IS_DEV = process.env.NODE_ENV === 'development';
// 防刷屏：同 (locale, key) 只警告一次
const warnedMissingKeys = IS_DEV ? new Set() : null;
// 缓存每个 locale 的"全部可加载模块名" Set，避免每次 missing 都重新扫 glob/context
const moduleNamesSetCache = IS_DEV ? new Map() : null;

const i18n = createI18n({
  legacy: false,
  locale: initialLocale,
  fallbackLocale: 'zh-CN',
  messages: { 'zh-CN': {}, 'en-US': {} },
  missing: (locale, key) => {
    if (IS_DEV) warnIfModuleNotLoaded(locale, key);
    return key;
  }
});

// 按语言分开声明 glob/context，确保 Vite / Webpack 将两种语言拆为独立 chunk 组
// Vite：import.meta.glob 默认懒加载（无 eager），返回 () => Promise<Module>
// Webpack：require.context 第四参数 'lazy'，ctx(key) 返回 Promise<Module>，每个 JSON 单独 chunk
const localeGlobMap = inVite()
  ? {
      'zh-CN': import.meta.glob('@/locales/zh-CN/*.json'),
      'en-US': import.meta.glob('@/locales/en-US/*.json')
    }
  : null;

// require.context 的第三个参数必须为正则字面量（webpack 编译期静态分析），不能抽成变量
// 此处正则用途：匹配目录下所有以 .json 结尾的文件，用于让 webpack 把每个语言包文件单独打成 lazy chunk
const localeContextMap = !inVite()
  ? {
      'zh-CN': require.context('@/locales/zh-CN', false, /\.json$/, 'lazy'),
      'en-US': require.context('@/locales/en-US', false, /\.json$/, 'lazy')
    }
  : null;

// 记录已加载的模块，防止重复加载
const loadedModules = new Set();

// 记录正在飞行中的单模块加载请求（key = `${lang}:${moduleName}`），避免并发场景下同一个 chunk 被多次发请求
const inflightModules = new Map();

/**
 * 加载单个模块并返回其消息对象，失败返回 null
 * 统一 Vite (import.meta.glob) 与 Webpack (require.context lazy) 两端的 import 形态
 * @param {string} lang - 语言标识
 * @param {string} moduleName - 模块名（不含 .json）
 * @returns {Promise<object|null>}
 */
async function loadLocaleModuleData(lang, moduleName) {
  if (inVite()) {
    const modules = localeGlobMap[lang];
    if (!modules) {
      console.warn(`[i18n] No glob defined for locale: ${lang}`);
      return null;
    }
    const matchKey = Object.keys(modules).find((p) => p.endsWith(`/${moduleName}.json`));
    if (!matchKey) {
      console.warn(`[i18n] Module not found: ${lang}/${moduleName}.json`);
      return null;
    }
    const mod = await modules[matchKey]();
    return mod.default || mod;
  }

  const ctx = localeContextMap[lang];
  if (!ctx) {
    console.warn(`[i18n] No context defined for locale: ${lang}`);
    return null;
  }
  try {
    // require.context lazy 模式：ctx(key) 返回 Promise<Module>，每个文件独立 chunk
    const mod = await ctx(`./${moduleName}.json`);
    return mod.default || mod;
  } catch (error) {
    console.warn(`[i18n] Module not found: ${lang}/${moduleName}.json`, error);
    return null;
  }
}

/**
 * 单模块加载的并发去重封装：
 * - 已加载过的模块直接返回 null（外层不需要再 merge）
 * - 正在飞行中的同 key 请求复用同一个 Promise，避免多次 import 同一个 chunk
 * - 成功后把 key 写入 loadedModules；无论成败最终都会从 inflightModules 移除
 * @param {string} lang
 * @param {string} moduleName
 * @returns {Promise<object|null>}
 */
function loadLocaleModuleOnce(lang, moduleName) {
  const key = `${lang}:${moduleName}`;
  if (loadedModules.has(key)) return Promise.resolve(null);

  const existing = inflightModules.get(key);
  if (existing) return existing;

  const promise = loadLocaleModuleData(lang, moduleName)
    .then((data) => {
      if (data) loadedModules.add(key);
      return data;
    })
    .finally(() => {
      inflightModules.delete(key);
    });
  inflightModules.set(key, promise);
  return promise;
}

/**
 * 加载指定语言的指定模块列表
 * @param {string} lang - 语言标识，如 'zh-CN'
 * @param {string[]} moduleNames - 要加载的模块名称列表
 */
export async function loadLocaleModules(lang, moduleNames) {
  const toLoad = moduleNames.filter((name) => !loadedModules.has(`${lang}:${name}`));
  if (toLoad.length === 0) return;

  try {
    const results = await Promise.all(toLoad.map((moduleName) => loadLocaleModuleOnce(lang, moduleName)));

    const loadedMessages = {};
    for (const data of results) {
      if (data) Object.assign(loadedMessages, data);
    }

    if (Object.keys(loadedMessages).length === 0) return;

    const existing = i18n.global.getLocaleMessage(lang);
    i18n.global.setLocaleMessage(lang, { ...existing, ...loadedMessages });
  } catch (error) {
    console.warn('[i18n] loadLocaleModules failed', { lang, moduleNames: toLoad, error });
  }
}

/**
 * 加载登录页模块（首屏最小集）
 * @param {string} lang - 语言标识
 */
export async function loadLoginLocale(lang) {
  await loadLocaleModules(lang, LOGIN_MODULES);
}

/**
 * 加载主布局模块（进入认证区域后调用一次）
 * @param {string} lang - 语言标识
 */
export async function loadLayoutLocale(lang) {
  await loadLocaleModules(lang, LAYOUT_MODULES);
}

/**
 * 加载核心模块（兼容旧调用，等价于 loadLoginLocale + loadLayoutLocale）
 * @param {string} lang - 语言标识
 */
export async function loadCoreLocale(lang) {
  await loadLocaleModules(lang, CORE_MODULES);
}

/**
 * 列出指定语言全部可加载模块名（自 glob/context 反推），保持 Vite 与 Webpack 行为一致
 * @param {string} lang - 语言标识
 * @returns {string[]}
 */
function listAllModuleNames(lang) {
  // 从形如 '/locales/zh-CN/common.json' 或 './common.json' 的路径中提取文件名（不含扩展名），
  // 用于把 glob/context 的 key 列表反推为 ['common', 'login', ...] 形式的模块名
  const pickModuleName = /\/([^/]+)\.json$/;
  if (inVite()) {
    const modules = localeGlobMap[lang];
    if (!modules) return [];
    return Object.keys(modules)
      .map((p) => p.match(pickModuleName)?.[1])
      .filter(Boolean);
  }
  const ctx = localeContextMap[lang];
  if (!ctx) return [];
  return ctx
    .keys()
    .map((k) => k.match(pickModuleName)?.[1])
    .filter(Boolean);
}

/**
 * 加载指定语言的全部模块（用于切换语言场景）
 * 改造后 Webpack 端同样走按需 chunk，不再一次性同步打包全部 JSON 进主 bundle
 * @param {string} lang - 语言标识
 */
export async function loadLanguage(lang) {
  // 防止重复加载
  if (Object.keys(i18n.global.getLocaleMessage(lang)).length > 0) {
    return;
  }

  try {
    const allModuleNames = listAllModuleNames(lang);
    if (allModuleNames.length === 0) {
      console.warn(`[i18n] No locale modules found for: ${lang}`);
      return;
    }
    await loadLocaleModules(lang, allModuleNames);
  } catch (error) {
    console.warn('[i18n] loadLanguage failed', { lang, error });
    if (lang !== FALLBACK_LOCALE) {
      try {
        await loadLanguage(FALLBACK_LOCALE);
      } catch (fallbackError) {
        console.error('[i18n] loadLanguage fallback failed', { lang: FALLBACK_LOCALE, error: fallbackError });
      }
    }
    if (Object.keys(i18n.global.getLocaleMessage(lang)).length === 0) {
      i18n.global.setLocaleMessage(lang, {});
    }
  }
}

/**
 * dev-only 运行时兜底：当 i18n key 命中 missing handler 时，
 * 若 key 形如 `<mod>.<sub>`，且 mod 是 locales 下真实存在的模块、不属于 CORE_MODULES、当前尚未加载，
 * 提示开发者很可能是路由 meta.localeModules 漏声明。
 *
 * 仅作为开发期辅助手段，不能替代提交门禁（见 code-standard/route-locale-modules.md）。
 * @param {string} locale - 当前语言
 * @param {string} key - 缺失的 i18n key
 */
function warnIfModuleNotLoaded(locale, key) {
  if (typeof key !== 'string' || !key.includes('.')) return;
  const mod = key.slice(0, key.indexOf('.'));
  if (!mod) return;

  // CORE_MODULES 在首屏加载期间会大量触发 missing（属正常排队等待），不是漏声明
  if (CORE_MODULES.includes(mod)) return;

  // 仅当 mod 是 locales 下真实存在的可加载模块时，才认为是"漏声明"信号
  let nameSet = moduleNamesSetCache.get(locale);
  if (!nameSet) {
    nameSet = new Set(listAllModuleNames(locale));
    moduleNamesSetCache.set(locale, nameSet);
  }
  if (!nameSet.has(mod)) return;

  // 已加载就不是漏声明（可能只是该模块里确实没有这条 key）
  if (loadedModules.has(`${locale}:${mod}`)) return;

  const dedupKey = `${locale}:${key}`;
  if (warnedMissingKeys.has(dedupKey)) return;
  warnedMissingKeys.add(dedupKey);

  console.warn(
    `[i18n] 模块 "${mod}" 未加载，但已使用 key="${key}"（locale="${locale}"）。\n` +
      `  → 检查当前路由的 meta.localeModules 是否声明了 "${mod}"。\n` +
      '  → 详见 code-standard/route-locale-modules.md'
  );
}

// 监听用户打开新标签页，切换语言
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    const cookieValue = getCookie(LANGUAGE_KEY);
    if (cookieValue) {
      const normalizedLang = normalizeLanguage(cookieValue);
      if (i18n.global.locale.value !== normalizedLang) {
        // 切换语言时加载全量
        loadLanguage(normalizedLang).then(() => {
          i18n.global.locale.value = normalizedLang;
          store.commit('setLang', normalizedLang);
        });
      }
    }
  }
});

export { CORE_MODULES };
export default i18n;
